import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { ANNOTATION_TOOL_ALLOWED_APPS } from '../lib';

/**
 * Custom hook to extract the current appId from the URL pathname.
 *
 * The URL structure is: /annotation-tool/{appId}
 *
 * @returns The current appId from the URL, or the first allowed app as fallback
 */
export const useAnnotationToolAppId = (): string | undefined => {
  const location = useLocation();

  return useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);

    // Find the segment that matches one of the allowed app IDs
    // The path should be: /annotation-tool/{appId}
    const annotationToolIndex = pathSegments.findIndex((segment) => segment === 'annotation-tool');

    if (annotationToolIndex >= 0 && pathSegments[annotationToolIndex + 1]) {
      const urlAppId = pathSegments[annotationToolIndex + 1];

      // Verify it's a valid appId from allowedApps
      const isValidAppId = ANNOTATION_TOOL_ALLOWED_APPS.some((app) => app.appId === urlAppId);

      if (isValidAppId) {
        return urlAppId;
      }
    }

    // Fallback to first allowed app if no valid appId found in URL
    return ANNOTATION_TOOL_ALLOWED_APPS[0]?.appId;
  }, [location.pathname]);
};
